/**
 * PostHog access from non-React code.
 *
 * `PostHogProvider` in src/index.tsx initializes the *default* posthog-js singleton
 * (@posthog/react calls `setDefaultPostHogInstance(posthogJs)` internally), so the
 * `posthog` imported here is the same object `usePostHog()` hands to components.
 * There is one instance and one config; nothing needs to be threaded through.
 *
 * Two consequences worth knowing:
 *
 * 1. The provider initializes inside a `useEffect`, so it has not run during the
 *    first render pass. Calling these at module scope would no-op silently — call
 *    them from event handlers, thunks, or effects, never at import time.
 * 2. `posthog.capture` before `init` is a no-op rather than a throw, which means a
 *    missing REACT_APP_POSTHOG_PROJECT_TOKEN loses events without any error. See
 *    client/.env.production for where the build-time values come from.
 *
 * Prefer `usePostHog()` inside components; this module is for Redux thunks and
 * plain utilities, which have no hook context.
 */
import posthog from "posthog-js";
import type { UserState } from "../slices/auth";

/**
 * Bind subsequent events to a known user.
 *
 * PostHog keeps its own anonymous distinct_id until told otherwise, so without
 * this the pre-login and post-login halves of a session look like two people.
 * Only non-sensitive profile fields belong here — everything set becomes queryable
 * person properties, and the app is behind a CookieYes consent banner.
 */
export function identifyUser(user: UserState): void {
  posthog.identify(user.id, {
    email: user.email,
    name: `${user.first_name} ${user.last_name}`.trim(),
  });
}

/**
 * Record the logout itself, then clear the identity.
 *
 * Order matters: the capture has to happen while the user is still identified, or
 * the event lands on a fresh anonymous id and the session looks like it ended on
 * its own.
 *
 * Without the reset, the next person on a shared browser inherits the previous
 * user's distinct_id and their events are attributed to that account. It also
 * regenerates the anonymous id, so it belongs on logout and nowhere else.
 */
export function trackLogout(): void {
  posthog.capture("logout");
  posthog.reset();
}
